import Phaser from 'phaser';
import { GAME_CONFIG } from '../config';
import { STAGES, getStage, getAccessoryForStage } from '../data/stages';
import { Character } from '../objects/Character';
import { stageProgress } from '../state/StageProgress';
import { BlinkSystem } from '../systems/BlinkSystem';
import { DanceSystem } from '../systems/DanceSystem';
import { COLORS, CSS, FONT, OUTLINE, RADIUS } from '../theme';
import { drawOutlinedRect, drawOutlinedCircle } from '../ui/OutlinedShape';
import { makePill } from '../ui/Pill';
import { fillBackdropStar } from '../utils/decorations';
import { getCharacterScaleFactor, getLayoutFrame } from '../utils/layout';
import { setLetterboxColor, setLetterboxOverlay } from '../utils/letterbox';

/**
 * Stage Pop v2 — HomeScreen.
 * Cream backdrop, profile header, big headline, character stage,
 * compact daily-challenge + friends-leaderboard cards side-by-side,
 * and bottom CTA buttons (Stages / Closet).
 */
export class HomeScene extends Phaser.Scene {
  private character: Character | null = null;
  private danceSystem: DanceSystem | null = null;
  private blinkSystem: BlinkSystem | null = null;
  private modalObjects: Phaser.GameObjects.GameObject[] | null = null;

  constructor() {
    super('HomeScene');
  }

  create() {
    this.character = null;
    this.danceSystem = null;
    this.blinkSystem = null;
    this.modalObjects = null;
    const { STAGE_W } = GAME_CONFIG;
    const frame = getLayoutFrame(this);
    this.drawBackdrop();

    // ----- Top header: avatar + name on left, points pill on right
    this.drawProfileHeader(40, frame.fromTop(80));
    makePill(this, STAGE_W - 110, frame.fromTop(80), {
      text: `✦ ${stageProgress.getPoints()}p`,
      textSize: 28,
      textColor: CSS.ink,
      fontWeight: '800',
      padX: 24,
      padY: 14,
      fill: COLORS.yolk,
      fillAlpha: 1,
      border: COLORS.ink,
      borderAlpha: 1,
      borderThickness: 4,
    });

    // ----- Settings gear (directly below the points pill)
    this.add
      .text(STAGE_W - 110, frame.fromTop(130), '⚙', {
        fontFamily: FONT.display,
        fontSize: '36px',
        color: CSS.ink,
        fontStyle: '900',
      })
      .setOrigin(0.5);
    const settingsHit = this.add
      .zone(STAGE_W - 110, frame.fromTop(130), 72, 72)
      .setInteractive({ useHandCursor: true });
    settingsHit.on('pointerup', () => this.scene.start('SettingsScene'));

    // ----- Title block
    this.add
      .text(STAGE_W / 2, frame.fromTop(232), 'Toss the hat', {
        fontFamily: FONT.display,
        fontSize: '62px',
        color: CSS.ink,
        fontStyle: '900',
      })
      .setOrigin(0.5);

    this.add
      .text(STAGE_W / 2, frame.fromTop(302), 'to Yodajjom!', {
        fontFamily: FONT.display,
        fontSize: '66px',
        color: CSS.coral,
        fontStyle: '900',
      })
      .setOrigin(0.5);

    // ----- Character stage (just below the title, hero of the screen)
    this.drawCharacterStage();

    // ----- Daily banner + Leaderboard stack.
    // The stack is shifted up ~40px from the original design so the visible
    // gap between the leaderboard's bottom shadow and the PLAY/CLOSET row
    // matches the banner↔leaderboard gap — without that lift the buttons
    // crowd the leaderboard's drop shadow on devices where the safe-area
    // bottom inset is small.
    this.drawDailyBanner(STAGE_W / 2, frame.fromTop(1018), 640, 72);
    this.drawLeaderboard(STAGE_W / 2, frame.fromTop(1193), 640, 220);

    // ----- Bottom CTA buttons (Stage / Closet)
    // Each button is 310 wide with a 20-px center gap, so the pair spans the
    // same 640-px footprint as the leaderboard panel — left edges land at
    // x=40, right edges at x=680 (matching the panel above).
    const btnY = frame.fromBottom(105);
    this.makeBigButton(
      STAGE_W / 2 - 165,
      btnY,
      310,
      'PLAY  ▶',
      'STAGES',
      COLORS.coral,
      CSS.white,
      () => this.scene.start('StageSelectScene'),
    );
    this.makeBigButton(
      STAGE_W / 2 + 165,
      btnY,
      310,
      '★ ★ ★',
      'CLOSET',
      COLORS.white,
      CSS.ink,
      () => this.scene.start('ClosetScene'),
    );

    this.game.events.on('safeAreaChanged', this.handleSafeAreaChanged, this);
    this.events.once('shutdown', () => {
      this.game.events.off('safeAreaChanged', this.handleSafeAreaChanged, this);
    });
  }

  private handleSafeAreaChanged = () => {
    if (!this.scene.isActive()) return;
    this.scene.restart();
  };

  update(time: number) {
    if (this.danceSystem) this.danceSystem.update(time);
  }

  // ────────────────────────────────────────────────────────────────────────
  // Character stage — idle dance between title and cards
  // ────────────────────────────────────────────────────────────────────────
  private drawCharacterStage() {
    const { STAGE_W } = GAME_CONFIG;
    const frame = getLayoutFrame(this);
    const character = new Character(this);
    this.character = character;

    const stageX = STAGE_W / 2;
    // Bumped 1.5× — the bigger sprite forced the floor up so the blanket
    // bottom (~stageY + 158 at this scale) sits above the daily banner top.
    const stageY = frame.fromTop(833);
    character.container.setPosition(stageX, stageY);
    character.worldX = stageX;
    character.worldY = stageY;

    const baseScale = 1.395 * getCharacterScaleFactor(frame);
    character.container.setScale(baseScale);

    this.danceSystem = new DanceSystem(character, {
      bpm: 95,
      maxAngle: 6,
      lateralPx: 0,
      depthPx: 0,
    });

    // Idle blink — gives the resting hero a hint of life. The system handles
    // its own scheduling on the scene's time/delayedCall, so no per-frame
    // hook is needed; existing update() only drives the dance angle.
    this.blinkSystem = new BlinkSystem(this, character);
  }

  // ────────────────────────────────────────────────────────────────────────
  // Backdrop
  // ────────────────────────────────────────────────────────────────────────
  private drawBackdrop() {
    const { STAGE_W, STAGE_H } = GAME_CONFIG;
    setLetterboxColor(COLORS.cream);

    // The dotted grid is ~1200 tiny fillCircles plus a yolk/coral star
    // sprinkle. Drawing it as live Graphics means tens of thousands of
    // triangles pushed every frame, which Android browsers struggle with
    // — the character dance reads as stutter. Bake it into a single
    // texture once and draw it as a single quad afterwards. The star
    // pattern (replacing the older round-blob accents) matches the
    // ResultScene backdrop so cream scenes share one decoration language.
    const key = 'home-backdrop-v3';
    if (!this.textures.exists(key)) {
      const g = this.make.graphics({ x: 0, y: 0 }, false);
      g.fillStyle(COLORS.cream, 1);
      g.fillRect(0, 0, STAGE_W, STAGE_H);

      g.fillStyle(COLORS.ink, 0.05);
      for (let yy = 0; yy < STAGE_H; yy += 28) {
        for (let xx = 14; xx < STAGE_W; xx += 28) {
          g.fillCircle(xx + (yy % 56 === 0 ? 14 : 0), yy, 1.5);
        }
      }

      // Hand-placed star positions tuned for the HomeScene layout — kept in
      // the left/right margins where the character body, daily banner, and
      // leaderboard panel don't already occupy the canvas. Stars that fall
      // behind opaque cards are naturally hidden by those cards' fills.
      const yolkStars = [
        { x: 80, y: 130, r: 20 },
        { x: 650, y: 110, r: 18 },
        { x: 50, y: 750, r: 18 },
        { x: 670, y: 720, r: 22 },
        { x: 70, y: 1060, r: 16 },
        { x: 660, y: 1080, r: 20 },
      ];
      yolkStars.forEach((s) => fillBackdropStar(g, s.x, s.y, s.r, COLORS.yolk, 0.35));
      const coralStars = [
        { x: 210, y: 170, r: 14 },
        { x: 530, y: 160, r: 16 },
        { x: 30, y: 910, r: 14 },
        { x: 690, y: 880, r: 12 },
      ];
      coralStars.forEach((s) => fillBackdropStar(g, s.x, s.y, s.r, COLORS.coral, 0.32));

      g.generateTexture(key, STAGE_W, STAGE_H);
      g.destroy();
    }
    this.add.image(STAGE_W / 2, STAGE_H / 2, key);
  }

  // ────────────────────────────────────────────────────────────────────────
  // Profile header (left): avatar disk + name + level
  // ────────────────────────────────────────────────────────────────────────
  private drawProfileHeader(x: number, y: number) {
    const r = 42;
    const cx = x + r + 4;
    drawOutlinedCircle(this, cx, y, r, COLORS.yolk, COLORS.ink, OUTLINE);

    if (this.textures.exists('head_main')) {
      const head = this.add.image(cx, y, 'head_main');
      const tex = head.texture.getSourceImage() as HTMLImageElement;
      const max = r * 1.6;
      const scale = Math.min(max / tex.width, max / tex.height);
      head.setScale(scale);
    }

    this.add
      .text(cx + r + 16, y - 16, 'Yodajjom', {
        fontFamily: FONT.display,
        fontSize: '32px',
        color: CSS.ink,
        fontStyle: '900',
      })
      .setOrigin(0, 0.5);

    const totalScore = stageProgress.totalCumulativeScore();
    const level = Math.max(1, Math.floor(totalScore / 200) + 1);
    this.add
      .text(cx + r + 16, y + 18, `Lv.${level}  ·  ${totalScore} pts`, {
        fontFamily: FONT.display,
        fontSize: '20px',
        color: CSS.inkSoft,
        fontStyle: '700',
      })
      .setOrigin(0, 0.5);
  }

  // ────────────────────────────────────────────────────────────────────────
  // Daily banner — slim one-row CTA above the character
  // ────────────────────────────────────────────────────────────────────────
  private drawDailyBanner(x: number, y: number, w: number, h: number) {
    const target =
      STAGES.find((s) => {
        if (!stageProgress.isUnlocked(s)) return false;
        const rec = stageProgress.getRecord(s.id);
        return rec.stars < 3;
      }) ?? STAGES[0];

    const stage = getStage(target.id) ?? STAGES[0];
    const acc = getAccessoryForStage(stage);

    drawOutlinedRect(this, x, y, w, h, {
      fill: COLORS.plum,
      radius: h / 2,
      shadow: 6,
      outline: OUTLINE,
    });

    // Accessory icon disk (left)
    const diskX = x - w / 2 + 44;
    drawOutlinedCircle(this, diskX, y, 26, COLORS.yolk, COLORS.ink, 3);
    if (this.textures.exists(acc.accIconKey)) {
      const icon = this.add.image(diskX, y, acc.accIconKey);
      const tex = icon.texture.getSourceImage() as HTMLImageElement;
      const max = 40;
      icon.setScale(Math.min(max / tex.width, max / tex.height));
    }

    // DAILY label + stage name (stacked, next to icon)
    const textX = diskX + 38;
    this.add
      .text(textX, y - 14, 'DAILY', {
        fontFamily: FONT.display,
        fontSize: '18px',
        color: CSS.yolk,
        fontStyle: '900',
      })
      .setOrigin(0, 0.5)
      .setLetterSpacing(2);
    this.add
      .text(textX, y + 14, stage.name, {
        fontFamily: FONT.display,
        fontSize: '26px',
        color: CSS.white,
        fontStyle: '900',
      })
      .setOrigin(0, 0.5);

    // PLAY pill (right)
    const playPillX = x + w / 2 - 70;
    makePill(this, playPillX, y, {
      text: 'PLAY ▶',
      textSize: 22,
      textColor: CSS.white,
      fontWeight: '900',
      padX: 20,
      padY: 12,
      fill: COLORS.coral,
      fillAlpha: 1,
      border: COLORS.ink,
      borderAlpha: 1,
      borderThickness: 3,
    });

    // Hit zone covers entire banner
    const hit = this.add.zone(x, y, w, h).setInteractive({ useHandCursor: true });
    hit.on('pointerup', () => this.scene.start('GameScene', { stageId: stage.id }));
  }

  // ────────────────────────────────────────────────────────────────────────
  // Friends leaderboard panel
  // ────────────────────────────────────────────────────────────────────────
  private drawLeaderboard(x: number, y: number, w: number, h: number) {
    drawOutlinedRect(this, x, y, w, h, {
      fill: COLORS.white,
      radius: 28,
      shadow: 8,
      outline: OUTLINE,
    });

    // Header strip
    this.add
      .text(x - w / 2 + 32, y - h / 2 + 36, 'LEADER BOARD', {
        fontFamily: FONT.display,
        fontSize: '28px',
        color: CSS.ink,
        fontStyle: '900',
      })
      .setOrigin(0, 0.5)
      .setLetterSpacing(2);

    // MORE pill (replaces "THIS WEEK"); opens the full leaderboard modal.
    const morePill = makePill(this, x + w / 2 - 60, y - h / 2 + 36, {
      text: 'MORE ▸',
      textSize: 18,
      textColor: CSS.ink,
      fontWeight: '900',
      padX: 16,
      padY: 9,
      fill: COLORS.yolk,
      fillAlpha: 1,
      border: COLORS.ink,
      borderAlpha: 1,
      borderThickness: 3,
    });
    const moreBounds = morePill.getBounds();
    this.add
      .zone(moreBounds.centerX, moreBounds.centerY, moreBounds.width + 12, moreBounds.height + 12)
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () => this.openLeaderboardModal());

    // Top-3 leaderboard (stubs blended with the player's actual total)
    const myScore = stageProgress.totalCumulativeScore();
    interface Row {
      name: string;
      score: number;
      avatarColor: number;
      isYou?: boolean;
    }
    const allRows: Row[] = [
      { name: 'Cheese Pup', score: 1820, avatarColor: COLORS.yolk },
      { name: 'Lime Pup', score: 1490, avatarColor: COLORS.emerald },
      { name: 'Coral Pup', score: 1240, avatarColor: COLORS.coral },
      { name: 'Plum Pup', score: 780, avatarColor: COLORS.plum },
      { name: 'You (Yodajjom)', score: myScore, avatarColor: COLORS.rose, isYou: true },
    ];
    allRows.sort((a, b) => b.score - a.score);
    const rows = allRows.slice(0, 3);

    // Spacing: avatar r=22 + outline → row visible bot = ry + 24. With h=220 LB
    // and rowH=54 starting at top+80, last row sits ~20px clear of the bottom
    // edge so rank 3 doesn't overlap the card outline.
    const rowH = 54;
    const startY = y - h / 2 + 80;
    rows.forEach((row, idx) => {
      const ry = startY + idx * rowH;
      const rank = idx + 1;

      // Rank chip
      const chipX = x - w / 2 + 46;
      drawOutlinedCircle(
        this,
        chipX,
        ry,
        22,
        rank === 1 ? COLORS.yolk : rank === 2 ? COLORS.rose : COLORS.cream,
        COLORS.ink,
        2,
      );
      this.add
        .text(chipX, ry, `${rank}`, {
          fontFamily: FONT.display,
          fontSize: '22px',
          color: CSS.ink,
          fontStyle: '900',
        })
        .setOrigin(0.5);

      // Avatar disk
      const avX = chipX + 48;
      drawOutlinedCircle(this, avX, ry, 22, row.avatarColor, COLORS.ink, 2);

      // Name
      this.add
        .text(avX + 36, ry, row.name, {
          fontFamily: FONT.display,
          fontSize: '26px',
          color: CSS.ink,
          fontStyle: row.isYou ? '900' : '700',
        })
        .setOrigin(0, 0.5);

      // Score (right-aligned)
      this.add
        .text(x + w / 2 - 36, ry, `${row.score} pts`, {
          fontFamily: FONT.display,
          fontSize: '24px',
          color: row.isYou ? CSS.coral : CSS.inkSoft,
          fontStyle: '900',
        })
        .setOrigin(1, 0.5);
    });
  }

  // ────────────────────────────────────────────────────────────────────────
  // Big bottom CTA helper
  // ────────────────────────────────────────────────────────────────────────
  private makeBigButton(
    x: number,
    y: number,
    width: number,
    icon: string,
    label: string,
    fill: number,
    labelColor: string,
    onClick: () => void,
  ) {
    const h = 108;
    drawOutlinedRect(this, x, y, width, h, {
      fill,
      radius: RADIUS.rounded,
      shadow: 10,
      outline: OUTLINE,
    });
    this.add
      .text(x, y - 18, icon, {
        fontFamily: FONT.display,
        fontSize: '30px',
        color: labelColor,
        fontStyle: '900',
      })
      .setOrigin(0.5)
      .setLetterSpacing(2);
    this.add
      .text(x, y + 22, label, {
        fontFamily: FONT.display,
        fontSize: '28px',
        color: labelColor,
        fontStyle: '800',
      })
      .setOrigin(0.5);
    const hit = this.add.zone(x, y, width, h).setInteractive({ useHandCursor: true });
    hit.on('pointerup', onClick);
  }

  // ────────────────────────────────────────────────────────────────────────
  // Full leaderboard modal (opened by the MORE pill on the home card)
  // ────────────────────────────────────────────────────────────────────────
  private openLeaderboardModal() {
    if (this.modalObjects) return;
    const { STAGE_W, STAGE_H } = GAME_CONFIG;
    const objs: Phaser.GameObjects.GameObject[] = [];
    const DEPTH = 1000;

    // Tint the letterbox + ad-reserve strip to the *effective* canvas-dim
    // color (cream × 0.45 + black × 0.55 ≈ #736c60) so the letterbox blends
    // into the in-canvas dim instead of reading as a darker ink band around
    // the modal.
    setLetterboxOverlay(true, 0x736c60);

    // Dimmed full-screen backdrop — tap anywhere outside the panel to close.
    const backdrop = this.add
      .rectangle(STAGE_W / 2, STAGE_H / 2, STAGE_W, STAGE_H, 0x000000, 0.55)
      .setInteractive()
      .setDepth(DEPTH);
    backdrop.on('pointerup', () => this.closeLeaderboardModal());
    objs.push(backdrop);

    const modalW = 640;
    const modalH = 800;
    const modalX = STAGE_W / 2;
    const modalY = STAGE_H / 2;

    const panel = drawOutlinedRect(this, modalX, modalY, modalW, modalH, {
      fill: COLORS.white,
      radius: 32,
      shadow: 12,
      outline: OUTLINE,
    });
    panel.setDepth(DEPTH + 1);
    objs.push(panel);

    const titleTxt = this.add
      .text(modalX, modalY - modalH / 2 + 64, 'LEADER BOARD', {
        fontFamily: FONT.display,
        fontSize: '38px',
        color: CSS.ink,
        fontStyle: '900',
      })
      .setOrigin(0.5)
      .setLetterSpacing(3)
      .setDepth(DEPTH + 2);
    objs.push(titleTxt);

    const subTxt = this.add
      .text(modalX, modalY - modalH / 2 + 112, 'THIS WEEK', {
        fontFamily: FONT.display,
        fontSize: '20px',
        color: CSS.inkSoft,
        fontStyle: '800',
      })
      .setOrigin(0.5)
      .setLetterSpacing(3)
      .setDepth(DEPTH + 2);
    objs.push(subTxt);

    const myScore = stageProgress.totalCumulativeScore();
    interface Row {
      name: string;
      score: number;
      avatarColor: number;
      isYou?: boolean;
    }
    const allRows: Row[] = [
      { name: 'Cheese Pup', score: 1820, avatarColor: COLORS.yolk },
      { name: 'Lime Pup', score: 1490, avatarColor: COLORS.emerald },
      { name: 'Coral Pup', score: 1240, avatarColor: COLORS.coral },
      { name: 'Plum Pup', score: 780, avatarColor: COLORS.plum },
      { name: 'You (Yodajjom)', score: myScore, avatarColor: COLORS.rose, isYou: true },
    ];
    allRows.sort((a, b) => b.score - a.score);

    const rowH = 76;
    const startY = modalY - modalH / 2 + 200;
    allRows.forEach((row, idx) => {
      const ry = startY + idx * rowH;
      const rank = idx + 1;

      const chipX = modalX - modalW / 2 + 60;
      const chipBg =
        rank === 1
          ? COLORS.yolk
          : rank === 2
          ? COLORS.rose
          : rank === 3
          ? COLORS.cream
          : COLORS.white;
      const chip = drawOutlinedCircle(this, chipX, ry, 26, chipBg, COLORS.ink, 3);
      chip.setDepth(DEPTH + 2);
      objs.push(chip);
      const rankTxt = this.add
        .text(chipX, ry, `${rank}`, {
          fontFamily: FONT.display,
          fontSize: '26px',
          color: CSS.ink,
          fontStyle: '900',
        })
        .setOrigin(0.5)
        .setDepth(DEPTH + 3);
      objs.push(rankTxt);

      const avX = chipX + 58;
      const avatar = drawOutlinedCircle(this, avX, ry, 26, row.avatarColor, COLORS.ink, 3);
      avatar.setDepth(DEPTH + 2);
      objs.push(avatar);

      const nameTxt = this.add
        .text(avX + 42, ry, row.name, {
          fontFamily: FONT.display,
          fontSize: '28px',
          color: CSS.ink,
          fontStyle: row.isYou ? '900' : '700',
        })
        .setOrigin(0, 0.5)
        .setDepth(DEPTH + 3);
      objs.push(nameTxt);

      const scoreTxt = this.add
        .text(modalX + modalW / 2 - 44, ry, `${row.score} pts`, {
          fontFamily: FONT.display,
          fontSize: '26px',
          color: row.isYou ? CSS.coral : CSS.inkSoft,
          fontStyle: '900',
        })
        .setOrigin(1, 0.5)
        .setDepth(DEPTH + 3);
      objs.push(scoreTxt);
    });

    // CLOSE button at the bottom of the modal.
    const closeY = modalY + modalH / 2 - 70;
    const closePill = makePill(this, modalX, closeY, {
      text: 'CLOSE',
      textSize: 24,
      textColor: CSS.ink,
      fontWeight: '900',
      padX: 44,
      padY: 16,
      fill: COLORS.coral,
      fillAlpha: 1,
      border: COLORS.ink,
      borderAlpha: 1,
      borderThickness: 3,
    });
    closePill.setDepth(DEPTH + 2);
    objs.push(closePill);
    const closeBounds = closePill.getBounds();
    const closeHit = this.add
      .zone(
        closeBounds.centerX,
        closeBounds.centerY,
        closeBounds.width + 16,
        closeBounds.height + 16,
      )
      .setInteractive({ useHandCursor: true })
      .setDepth(DEPTH + 4);
    closeHit.on('pointerup', () => this.closeLeaderboardModal());
    objs.push(closeHit);

    this.modalObjects = objs;
  }

  private closeLeaderboardModal() {
    if (!this.modalObjects) return;
    this.modalObjects.forEach((o) => o.destroy());
    this.modalObjects = null;
    setLetterboxOverlay(false);
  }
}
